'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { Menu, X, BookOpen, BarChart2, GraduationCap, Search, LayoutDashboard, Calendar, Brain, Phone } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import CounsellingModal from '@/components/CounsellingModal';

const publicNavLinks = [
  { href: '/', label: 'Home', icon: BookOpen },
  { href: '/neet-college-predictor', label: 'NEET Predictor', icon: GraduationCap },
  { href: '/rank-predictor', label: 'Rank', icon: BarChart2 },
  { href: '/medical-colleges-database', label: 'Colleges', icon: Search },
  { href: '/admission-calendar', label: 'Calendar', icon: Calendar },
];

export default function Navbar() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showCounsellingModal, setShowCounsellingModal] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { user, signOut } = useAuth();

  React.useEffect(() => {
    setMounted(true);
  }, []);

  React.useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Use auth state only after mount to prevent hydration mismatch
  const isSignedIn = mounted ? !!user : false;

  const navLinks = isSignedIn
    ? [...publicNavLinks, { href: '/student-dashboard', label: 'Dashboard', icon: LayoutDashboard }]
    : publicNavLinks;

  return (
    <>
      {showCounsellingModal && (
        <CounsellingModal onClose={() => setShowCounsellingModal(false)} source="Navbar" />
      )}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-[#0a1f5c] shadow-lg shadow-[#0a1f5c]/20'
            : 'bg-white border-b border-[#0a1f5c]/10'
        }`}
      >
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6">
          <div className="flex items-center h-16 gap-4">
            {/* Logo */}
            <Link href="/" className="flex items-center flex-shrink-0">
              <Image
                src="/assets/images/WE-LOGO-1-1782729563376.png"
                alt="Wikas Education Logo"
                width={120}
                height={56}
                className="object-contain h-12 w-auto"
                priority
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-0.5 flex-1 justify-center">
              {navLinks?.map((link) => {
                const isActive = pathname === link?.href;
                return (
                  <Link
                    key={`nav-${link?.href}`}
                    href={link?.href}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      isActive
                        ? scrolled
                          ? 'text-white bg-white/15 font-semibold' :'text-[#1a3a8f] bg-[#1a3a8f]/8 font-semibold'
                        : scrolled
                          ? 'text-blue-100 hover:text-white hover:bg-white/10' :'text-slate-600 hover:text-[#0a1f5c] hover:bg-[#0a1f5c]/5'
                    }`}
                  >
                    <link.icon size={14} />
                    {link?.label}
                  </Link>
                );
              })}
            </div>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-2 flex-shrink-0 ml-auto">
              {isSignedIn ? (
                <>
                  <Link
                    href="/admin/login"
                    className={`text-sm px-3 py-2 rounded-xl flex items-center gap-1.5 border font-semibold transition-all ${
                      scrolled
                        ? 'border-white/20 text-white hover:bg-white/10' :'border-[#0a1f5c]/20 text-[#0a1f5c] hover:bg-[#0a1f5c]/5'
                    }`}
                  >
                    <Brain size={13} />
                    Admin
                  </Link>
                  <button
                    onClick={signOut}
                    className={`text-sm px-4 py-2 rounded-xl border font-semibold transition-all ${
                      scrolled
                        ? 'border-white/20 text-white hover:bg-white/10' :'border-[#0a1f5c]/20 text-[#0a1f5c] hover:bg-[#0a1f5c]/5'
                    }`}
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <Link
                  href="/student-dashboard"
                  className={`text-sm px-4 py-2 rounded-xl border font-semibold transition-all ${
                    scrolled
                      ? 'border-white/20 text-white hover:bg-white/10' :'border-[#0a1f5c]/20 text-[#0a1f5c] hover:bg-[#0a1f5c]/5'
                  }`}
                >
                  Sign In
                </Link>
              )}
              <button
                onClick={() => setShowCounsellingModal(true)}
                className="btn-primary text-sm px-4 py-2 rounded-xl whitespace-nowrap flex items-center gap-1.5"
              >
                <Phone size={13} />
                Book Counselling
              </button>
            </div>

            {/* Mobile menu button */}
            <button
              className={`lg:hidden ml-auto p-2 rounded-lg border transition-colors ${
                scrolled
                  ? 'border-white/20 text-white hover:bg-white/10' :'border-slate-200 text-slate-600 hover:bg-slate-100'
              }`}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-slate-100 shadow-lg">
            <div className="max-w-screen-xl mx-auto px-4 py-3 space-y-1">
              {navLinks?.map((link) => {
                const isActive = pathname === link?.href;
                return (
                  <Link
                    key={`mobile-nav-${link?.href}`}
                    href={link?.href}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                      isActive
                        ? 'text-[#1a3a8f] bg-[#1a3a8f]/8 font-semibold'
                        : 'text-slate-600 hover:text-[#0a1f5c] hover:bg-slate-50'
                    }`}
                  >
                    <link.icon size={16} />
                    {link?.label}
                  </Link>
                );
              })}
              <div className="pt-2 pb-1 flex flex-col gap-2">
                {isSignedIn ? (
                  <button
                    onClick={() => { signOut(); setMobileOpen(false); }}
                    className="border border-[#0a1f5c]/20 text-[#0a1f5c] text-center text-sm py-3 rounded-xl w-full font-semibold"
                  >
                    Sign Out
                  </button>
                ) : (
                  <Link
                    href="/student-dashboard"
                    onClick={() => setMobileOpen(false)}
                    className="border border-[#0a1f5c]/20 text-[#0a1f5c] text-center text-sm py-3 rounded-xl font-semibold"
                  >
                    Sign In
                  </Link>
                )}
                <button
                  onClick={() => { setMobileOpen(false); setShowCounsellingModal(true); }}
                  className="btn-primary text-center text-sm py-3 rounded-xl flex items-center justify-center gap-2"
                >
                  <Phone size={15} />
                  Book Free Counselling
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}